/**
 * iter317 — Seller Reviews & Ratings UI.
 *
 * Two surfaces:
 *   - <SellerRatingBadge>: compact "4.8 ★ · 12 reviews" inline pill
 *     used on Ad Details + My Account / My Ads. Tapping it opens
 *     <SellerReviewsSheet> below.
 *   - <SellerReviewsSheet>: full list + report buttons.
 *   - <LeaveReviewModal>: 1-5 stars + tag chips + comment, gated by
 *     /api/me/review-eligibility/{seller_user_id}.
 *
 * The components share a single fetch hook so the badge and the sheet
 * can re-use the same in-memory aggregate (one HTTP call per render
 * pair).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Star, X, Flag, AlertTriangle, CheckCircle2 } from 'lucide-react';
import './SellerReviews.css';

const API = process.env.REACT_APP_BACKEND_URL;

// iter348 — Safe backdrop close.
// A modal opened by the same tap that lands on a fresh backdrop will
// otherwise immediately re-close because the synthesised `click`
// resolves against the newly-mounted element under the pointer. Track
// where the pointer went DOWN; only fire onClose if the pointer both
// down- and up-ed on the same backdrop element.
function useSafeBackdropClose(onClose) {
  const originRef = useRef(null);
  const onPointerDown = useCallback((e) => {
    // Only trust downs whose target IS the backdrop itself. Downs on
    // children (there shouldn't be any) are ignored.
    originRef.current = (e.target === e.currentTarget) ? e.currentTarget : null;
  }, []);
  const onClick = useCallback((e) => {
    // The tap must have both started AND ended on this exact backdrop.
    if (originRef.current === e.currentTarget && e.target === e.currentTarget) {
      onClose?.();
    }
    originRef.current = null;
  }, [onClose]);
  return { onPointerDown, onClick };
}

const POSITIVE_TAGS = [
  // iter346 — Operator spec: 4 positive tags. Labels are localized in
  // i18n so backend keys stay stable across renames.
  'responsive_seller',      // "Good communication"
  'accurate_description',   // "Item as described"
  'fair_price',
  'fast_response',
];
const NEGATIVE_TAGS = [
  // iter346 — Operator spec: 2 negative tags only.
  'not_responsive',         // "Bad communication"
  'wrong_description',      // "Not as described"
];

function authHeaders() {
  const tok = (typeof window !== 'undefined') ? localStorage.getItem('token') : '';
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

// ─────────────────────────────────────────────────────────────────────
// Shared hook — fetches the public aggregate. Refetch on demand.
// ─────────────────────────────────────────────────────────────────────
export function useSellerReviewSummary(sellerUserId) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!sellerUserId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/sellers/${sellerUserId}/review-summary`);
      if (r.ok) setSummary(await r.json());
    } catch (_) {
      /* swallowed — UI just falls back to "no reviews yet" */
    } finally {
      setLoading(false);
    }
  }, [sellerUserId]);

  useEffect(() => { refresh(); }, [refresh]);
  return { summary, loading, refresh };
}

// iter349 — Shared eligibility hook. Fetched by SellerReviewsSheet and
// the Ad Details "Rate seller" button so the CTA state and the modal
// use the same source of truth. This is what killed the previous
// "flash then warning" bug — the caller can decide whether to open
// the modal *before* opening it, instead of the modal opening blind
// and then swapping its body when the response lands.
//
// Returns:
//   { eligibility, status, refresh }
//   - eligibility = { eligible: bool, reason: string, already_reviewed: bool,
//                     existing_review_id: string | null } | null
//   - status = 'idle' | 'loading' | 'ready' | 'unauthenticated'
export function useSellerReviewEligibility(sellerUserId, enabled = true) {
  const [eligibility, setEligibility] = useState(null);
  const [status, setStatus] = useState('idle');

  const refresh = useCallback(async () => {
    if (!sellerUserId || !enabled) return;
    const headers = authHeaders();
    if (!headers.Authorization) {
      setStatus('unauthenticated');
      setEligibility({ eligible: false, reason: 'not_authenticated' });
      return;
    }
    setStatus('loading');
    try {
      const r = await fetch(
        `${API}/api/me/review-eligibility/${sellerUserId}`,
        { headers }
      );
      if (r.status === 401) {
        setStatus('unauthenticated');
        setEligibility({ eligible: false, reason: 'not_authenticated' });
        return;
      }
      if (r.ok) {
        setEligibility(await r.json());
        setStatus('ready');
      } else {
        setEligibility({ eligible: false, reason: 'error' });
        setStatus('ready');
      }
    } catch (_) {
      setEligibility({ eligible: false, reason: 'error' });
      setStatus('ready');
    }
  }, [sellerUserId, enabled]);

  useEffect(() => { refresh(); }, [refresh]);
  return { eligibility, status, refresh };
}

// ─────────────────────────────────────────────────────────────────────
// Inline badge — drop-in for any seller card / Ad Details / My Ads.
// ─────────────────────────────────────────────────────────────────────
export function SellerRatingBadge({ sellerUserId, onOpen }) {
  const { t } = useTranslation();
  const { summary } = useSellerReviewSummary(sellerUserId);
  const count = summary?.review_count || 0;
  const avg = summary?.average_rating || 0;

  if (count === 0) {
    return (
      <button
        type="button"
        className="seller-rating-badge seller-rating-badge--empty"
        onClick={onOpen}
        data-testid={`seller-rating-badge-${sellerUserId}`}
        data-count="0"
      >
        <Star size={13} />
        <span>{t('reviews.noReviewsYet')}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="seller-rating-badge"
      onClick={onOpen}
      data-testid={`seller-rating-badge-${sellerUserId}`}
      data-count={count}
      data-average={avg}
    >
      <Star size={13} fill="#f59e0b" stroke="#f59e0b" />
      <strong>{avg.toFixed(1)}</strong>
      <span className="seller-rating-badge__sep">·</span>
      <span>{t('reviews.countLabel', { count })}</span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Review list sheet — opens from the badge.
// ─────────────────────────────────────────────────────────────────────
export function SellerReviewsSheet({ sellerUserId, open, onClose, onLeaveReview }) {
  const { t, i18n } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [busyId, setBusyId] = useState('');
  const { summary, refresh } = useSellerReviewSummary(sellerUserId);
  // iter349 — Prefetch eligibility only when the sheet is open. This
  // lets the CTA label + click behavior reflect the true user state
  // BEFORE the modal is opened, killing the "form flashes then turns
  // into warning" bug.
  const { eligibility, status: eligStatus } = useSellerReviewEligibility(
    sellerUserId,
    open && !!onLeaveReview,
  );
  const isAr = i18n.language === 'ar';
  const safeBackdrop = useSafeBackdropClose(onClose);

  useEffect(() => {
    if (!open || !sellerUserId) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/sellers/${sellerUserId}/reviews`);
        if (r.ok) setReviews((await r.json()).reviews || []);
      } catch (_) { /* ignored */ }
    })();
  }, [open, sellerUserId]);

  // iter336 — Lock body scroll while the sheet is open so the page
  // behind doesn't pan when the user drags inside the review list,
  // AND mark the body with `.has-modal-open` so global CSS can hide
  // the BottomNav + Post-Ad FAB (they previously stomped the sheet
  // because their z-index was 9990 and the sheet's was 1200).
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('has-modal-open');
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('has-modal-open');
    };
  }, [open]);

  const onReport = useCallback(async (reviewId) => {
    if (busyId) return;
    setBusyId(reviewId);
    try {
      await fetch(`${API}/api/reviews/${reviewId}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ reason: 'flagged_by_buyer' }),
      });
      refresh();
    } finally {
      setBusyId('');
    }
  }, [busyId, refresh]);

  if (!open) return null;
  // iter336 — Portal at body level so the sheet never gets trapped
  // inside an Ad Details stacking context (was a contributing factor
  // to the operator's "content not visible" report).
  return createPortal(
    <div className="seller-reviews-sheet" role="dialog" aria-modal="true" data-testid="seller-reviews-sheet">
      <div
        className="seller-reviews-sheet__backdrop"
        onPointerDown={safeBackdrop.onPointerDown}
        onClick={safeBackdrop.onClick}
        data-testid="reviews-sheet-backdrop"
      />
      <div className={`seller-reviews-sheet__panel ${isAr ? 'is-rtl' : ''}`} dir={isAr ? 'rtl' : 'ltr'}>
        <header className="seller-reviews-sheet__header">
          <div>
            <h3>{t('reviews.sheetTitle')}</h3>
            {summary?.review_count > 0 && (
              <p className="seller-reviews-sheet__summary" data-testid="reviews-sheet-summary">
                <Star size={14} fill="#f59e0b" stroke="#f59e0b" />
                <strong>{summary.average_rating.toFixed(1)}</strong>
                <span>·</span>
                <span>{t('reviews.countLabel', { count: summary.review_count })}</span>
                {summary.positive_percent >= 80 && (
                  <span className="seller-reviews-sheet__chip">
                    <CheckCircle2 size={12} /> {summary.positive_percent}% {t('reviews.positivePct')}
                  </span>
                )}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label={t('close')} data-testid="reviews-sheet-close">
            <X size={20} />
          </button>
        </header>

        <div className="seller-reviews-sheet__body">
          {reviews.length === 0 ? (
            <div className="seller-reviews-sheet__empty" data-testid="reviews-sheet-empty">
              <Star size={40} stroke="#f59e0b" fill="#fef3c7" />
              <p className="seller-reviews-sheet__empty-title">
                {isAr ? 'لا توجد تقييمات بعد' : 'No reviews yet'}
              </p>
              <p className="seller-reviews-sheet__empty-sub">
                {isAr ? 'ستظهر تقييمات المشترين هنا.' : 'Reviews from buyers will appear here.'}
              </p>
              {/* iter346 — Inline "Be the first to review this seller" CTA
                  right in the empty state so buyers have a clear next
                  action instead of an inert placeholder. Hidden when the
                  viewer is the seller (onLeaveReview is null in that case
                  because AdDetails does not pass it). */}
              {onLeaveReview && (() => {
                // iter349 — Render the correct affordance based on
                // *known* eligibility. While loading show a subtle
                // skeleton pill (never the primary CTA that would
                // flash and immediately dead-end). Once known:
                //   • not_authenticated → sign-in prompt
                //   • no_interaction   → static warning card (NO button)
                //   • self_review      → nothing (owner shouldn't see this
                //                         anyway; AdDetails guards it)
                //   • eligible + no existing review → primary CTA
                //   • eligible + already reviewed   → "Update your review"
                if (eligStatus === 'idle' || eligStatus === 'loading') {
                  return (
                    <div
                      className="seller-reviews-sheet__cta-skeleton"
                      data-testid="reviews-sheet-cta-loading"
                      aria-hidden="true"
                    />
                  );
                }
                const reason = eligibility?.reason;
                if (!eligibility?.eligible) {
                  if (reason === 'self_review') return null;
                  return (
                    <div
                      className="seller-reviews-sheet__warning"
                      data-testid="reviews-sheet-warning"
                      role="status"
                    >
                      <AlertTriangle size={16} />
                      <p>
                        {reason === 'not_authenticated'
                          ? t('reviews.gate.notAuthenticated')
                          : t('reviews.gate.needsInteraction')}
                      </p>
                    </div>
                  );
                }
                const alreadyReviewed = !!eligibility?.already_reviewed;
                return (
                  <button
                    type="button"
                    className="seller-reviews-sheet__empty-cta"
                    onClick={onLeaveReview}
                    data-testid="reviews-sheet-empty-cta"
                  >
                    <Star size={14} />
                    <span>
                      {alreadyReviewed
                        ? t('reviews.leaveReviewCta')
                        : t('reviews.beTheFirstToReview')}
                    </span>
                  </button>
                );
              })()}
            </div>
          ) : (
            reviews.map((rv) => (
              <article
                key={rv.review_id}
                className="seller-review-row"
                data-testid={`seller-review-${rv.review_id}`}
              >
                <header>
                  <span className="seller-review-row__name">{rv.reviewer_name || t('reviews.anonymousBuyer')}</span>
                  <span className="seller-review-row__stars" aria-label={`${rv.rating}/5`}>
                    {[1,2,3,4,5].map(i => (
                      <Star
                        key={i}
                        size={13}
                        fill={i <= rv.rating ? '#f59e0b' : 'transparent'}
                        stroke={i <= rv.rating ? '#f59e0b' : '#cbd5e1'}
                      />
                    ))}
                  </span>
                </header>
                {rv.comment && <p className="seller-review-row__comment">{rv.comment}</p>}
                {rv.tags?.length > 0 && (
                  <ul className="seller-review-row__tags">
                    {rv.tags.map(tag => (
                      <li key={tag} data-tag={tag}>{t(`reviews.tag.${tag}`)}</li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  className="seller-review-row__report"
                  onClick={() => onReport(rv.review_id)}
                  disabled={busyId === rv.review_id}
                  data-testid={`report-review-${rv.review_id}`}
                  aria-label={t('reviews.report')}
                >
                  <Flag size={12} /> {t('reviews.report')}
                </button>
              </article>
            ))
          )}
        </div>

        {/* iter346/iter349 — The sticky footer only surfaces when the
            list is non-empty AND the current viewer is eligible.
            Ineligible viewers see the warning inline in the list
            itself (below), matching the empty-state behavior. */}
        {onLeaveReview && reviews.length > 0 && (() => {
          if (eligStatus === 'idle' || eligStatus === 'loading') return null;
          const reason = eligibility?.reason;
          if (!eligibility?.eligible) {
            if (reason === 'self_review') return null;
            return (
              <footer className="seller-reviews-sheet__footer seller-reviews-sheet__footer--warning">
                <div
                  className="seller-reviews-sheet__warning"
                  data-testid="reviews-sheet-warning"
                  role="status"
                >
                  <AlertTriangle size={16} />
                  <p>
                    {reason === 'not_authenticated'
                      ? t('reviews.gate.notAuthenticated')
                      : t('reviews.gate.needsInteraction')}
                  </p>
                </div>
              </footer>
            );
          }
          return (
            <footer className="seller-reviews-sheet__footer">
              <button
                type="button"
                className="seller-reviews-sheet__primary"
                onClick={onLeaveReview}
                data-testid="reviews-sheet-leave-review"
              >
                {eligibility?.already_reviewed
                  ? t('reviews.leaveReviewCta')
                  : t('reviews.leaveReviewCta')}
              </button>
            </footer>
          );
        })()}
      </div>
    </div>,
    document.body
  );
}

// ─────────────────────────────────────────────────────────────────────
// Leave-review modal (the actual form). Gated by eligibility precheck.
// ─────────────────────────────────────────────────────────────────────
export function LeaveReviewModal({ sellerUserId, adId, open, onClose, onSubmitted }) {
  const { t } = useTranslation();
  const [eligibility, setEligibility] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [tags, setTags] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const safeBackdrop = useSafeBackdropClose(onClose);

  // iter339 — Mirror the iter336 SellerReviewsSheet fix on the
  // *write* surface too: while the modal is open, mark the body so
  // global CSS hides the BottomNav + orange FAB. Without this the
  // bottom chrome covered the rating stars + Submit button on short
  // phones, exactly the bug the operator reported.
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add('has-modal-open');
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.classList.remove('has-modal-open');
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !sellerUserId) return;
    setError('');
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/me/review-eligibility/${sellerUserId}`,
          { headers: { ...authHeaders() } }
        );
        if (r.status === 401) {
          setEligibility({ eligible: false, reason: 'not_authenticated' });
          return;
        }
        if (r.ok) {
          const data = await r.json();
          setEligibility(data);
          // iter358 — Pre-fill the form with the buyer's existing review
          // so "update review" edits instead of starting blank. Fresh
          // opens with no prior review reset to a blank form.
          const ex = data?.existing_review;
          if (ex) {
            setRating(Number(ex.rating) || 0);
            setComment(typeof ex.comment === 'string' ? ex.comment : '');
            setTags(Array.isArray(ex.tags) ? ex.tags : []);
          } else {
            setRating(0);
            setComment('');
            setTags([]);
          }
        }
      } catch (_) {
        setEligibility({ eligible: false, reason: 'error' });
      }
    })();
  }, [open, sellerUserId]);

  const toggleTag = useCallback((tag) => {
    setTags(prev => prev.includes(tag) ? prev.filter(x => x !== tag) : [...prev, tag]);
  }, []);

  const submit = useCallback(async () => {
    if (busy) return;
    if (rating < 1 || rating > 5) { setError(t('reviews.errorRating')); return; }
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          seller_user_id: sellerUserId,
          rating,
          comment: comment.trim() || null,
          tags,
          ad_id: adId || null,
        }),
      });
      if (r.status === 401) { setError(t('reviews.errorAuth')); return; }
      if (r.status === 403) { setError(t('reviews.errorGate')); return; }
      if (r.status === 400) {
        const d = await r.json().catch(() => ({}));
        setError(d.detail || t('reviews.errorGeneric'));
        return;
      }
      if (!r.ok) { setError(t('reviews.errorGeneric')); return; }
      const body = await r.json();
      if (onSubmitted) onSubmitted(body);
      onClose?.();
    } catch (_) {
      setError(t('reviews.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }, [busy, rating, comment, tags, sellerUserId, adId, onClose, onSubmitted, t]);

  if (!open) return null;

  // iter349 — Never render the form while eligibility is unknown.
  // Rendering the form and then swapping it for a warning caused the
  // "form flashes for a second, then disappears" bug the operator
  // reported. Loading spinner instead until we know.
  const eligLoading = eligibility === null;
  const ineligible = eligibility && !eligibility.eligible;

  // iter339 — Portal mount so the modal escapes any parent stacking
  // context (e.g. Ad Details image carousel that sets its own
  // z-index). Combined with z-index 10001 on the wrapper this keeps
  // the sheet above the BottomNav and orange FAB on every page.
  return createPortal(
    <div className="leave-review-modal" role="dialog" aria-modal="true" data-testid="leave-review-modal">
      <div
        className="leave-review-modal__backdrop"
        onPointerDown={safeBackdrop.onPointerDown}
        onClick={safeBackdrop.onClick}
        data-testid="leave-review-backdrop"
      />
      <div className="leave-review-modal__panel">
        <header className="leave-review-modal__header">
          <h3>{t('reviews.modalTitle')}</h3>
          <button type="button" onClick={onClose} aria-label={t('close')} data-testid="leave-review-close">
            <X size={20} />
          </button>
        </header>

        {/* iter339 — Scrollable body wrapper so the stars / comment /
            tags / error never get clipped on short phones. Header
            stays pinned at top, footer stays pinned at bottom. */}
        <div className="leave-review-modal__body">
        {eligLoading ? (
          <div className="leave-review-modal__loading" data-testid="leave-review-loading" role="status" aria-live="polite">
            <div className="leave-review-modal__spinner" aria-hidden="true" />
            <p>{t('loading')}</p>
          </div>
        ) : ineligible ? (
          <div className="leave-review-modal__ineligible" data-testid="leave-review-ineligible">
            <AlertTriangle size={20} />
            <p>
              {eligibility.reason === 'self_review'
                ? t('reviews.gate.selfReview')
                : eligibility.reason === 'not_authenticated'
                  ? t('reviews.gate.notAuthenticated')
                  : t('reviews.gate.needsInteraction')}
            </p>
          </div>
        ) : (
          <>
            <div className="leave-review-modal__row">
              <label>{t('reviews.starsLabel')}</label>
              <div className="leave-review-stars" data-testid="leave-review-stars">
                {[1,2,3,4,5].map(i => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setRating(i)}
                    aria-label={`${i}/5`}
                    data-testid={`leave-review-star-${i}`}
                    data-selected={i <= rating}
                    className="leave-review-stars__btn"
                  >
                    <Star
                      size={28}
                      fill={i <= rating ? '#f59e0b' : 'transparent'}
                      stroke={i <= rating ? '#f59e0b' : '#cbd5e1'}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="leave-review-modal__row">
              <label htmlFor="leave-review-comment">{t('reviews.commentLabel')}</label>
              <textarea
                id="leave-review-comment"
                rows={3}
                maxLength={500}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('reviews.commentPlaceholder')}
                data-testid="leave-review-comment"
              />
              <span className="leave-review-modal__count">{comment.length}/500</span>
            </div>

            <div className="leave-review-modal__row">
              <label>{t('reviews.positiveTagsLabel')}</label>
              <ul className="leave-review-tags">
                {POSITIVE_TAGS.map(tag => (
                  <li key={tag}>
                    <button
                      type="button"
                      data-selected={tags.includes(tag)}
                      onClick={() => toggleTag(tag)}
                      data-testid={`leave-review-tag-${tag}`}
                    >
                      {t(`reviews.tag.${tag}`)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="leave-review-modal__row">
              <label>{t('reviews.negativeTagsLabel')}</label>
              <ul className="leave-review-tags leave-review-tags--negative">
                {NEGATIVE_TAGS.map(tag => (
                  <li key={tag}>
                    <button
                      type="button"
                      data-selected={tags.includes(tag)}
                      onClick={() => toggleTag(tag)}
                      data-testid={`leave-review-tag-${tag}`}
                    >
                      {t(`reviews.tag.${tag}`)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {error && (
              <p className="leave-review-modal__error" data-testid="leave-review-error">{error}</p>
            )}
          </>
        )}
        </div>

        {/* iter339/iter349 — Footer is rendered OUTSIDE the scrollable body so
            it always sticks to the bottom of the panel and never gets
            scrolled off-screen. The Submit button stays one tap away
            even with long comment + all tags selected. Show only when
            the seller is eligible AND eligibility is known; the
            ineligibility banner renders inside the scroll body and
            the modal closes via the X. */}
        {!eligLoading && !ineligible && (
          <footer className="leave-review-modal__footer">
            <button
              type="button"
              className="leave-review-modal__submit"
              onClick={submit}
              disabled={busy || rating < 1}
              data-testid="leave-review-submit"
            >
              {busy ? t('reviews.submitting') : t('reviews.submit')}
            </button>
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
